/*
 * Studios — "Find my nearest"
 * Geocodes a US ZIP code with the free Zippopotam.us API, or uses the browser's
 * location, then sorts the studio cards by straight-line distance and highlights the
 * closest one. All customer-facing text comes from section settings (data-* attributes).
 */
(function () {
  const ZIP_CODE = /^(\d{5})(?:-?\d{4})?$/;

  function distanceMiles(lat1, lng1, lat2, lng2) {
    const toRad = (d) => (d * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 3958.8 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  function fill(template, values) {
    return (template || '').replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));
  }

  function escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }

  async function geocodeZip(raw) {
    const match = raw.trim().match(ZIP_CODE);
    if (!match) throw new Error('invalid');
    const res = await fetch(`https://api.zippopotam.us/us/${match[1]}`);
    if (res.status === 404) throw new Error('not-found');
    if (!res.ok) throw new Error('service');
    const place = (await res.json()).places?.[0];
    if (!place || isNaN(parseFloat(place.latitude))) throw new Error('not-found');
    return { lat: parseFloat(place.latitude), lng: parseFloat(place.longitude), label: match[1] };
  }

  function init(section) {
    const form = section.querySelector('[data-finder-form]');
    if (!form) return;
    const input = section.querySelector('[data-finder-input]');
    const submit = form.querySelector('button[type="submit"]');
    const geoBtn = section.querySelector('[data-finder-geo]');
    const result = section.querySelector('[data-finder-result]');
    const text = section.dataset;
    const list = section.querySelector('[data-studio-list]');
    const studios = Array.from(list.querySelectorAll('[data-studio]')).filter(
      (el) => el.dataset.lat !== '' && el.dataset.lng !== '' && !isNaN(parseFloat(el.dataset.lat))
    );

    function formatMiles(miles) {
      if (miles < 0.1) return text.textMilesMin;
      return fill(text.textMiles, { miles: miles < 10 ? miles.toFixed(1) : Math.round(miles) });
    }

    function showMessage(html, isError) {
      result.innerHTML = html;
      result.classList.toggle('london-studios__result--error', !!isError);
    }

    function setLoading(loading) {
      submit.disabled = loading;
      if (geoBtn) geoBtn.disabled = loading;
      if (loading) showMessage(escapeHtml(text.textLoading));
    }

    function rank(origin, originLabel) {
      const ranked = studios
        .map((el) => ({ el, miles: distanceMiles(origin.lat, origin.lng, parseFloat(el.dataset.lat), parseFloat(el.dataset.lng)) }))
        .sort((a, b) => a.miles - b.miles);

      ranked.forEach(({ el, miles }, i) => {
        list.appendChild(el);
        el.classList.toggle('is-nearest', i === 0);
        const badge = el.querySelector('[data-studio-badge]');
        badge.hidden = i !== 0;
        badge.textContent = i === 0 ? text.textNearest : '';
        const distance = el.querySelector('[data-studio-distance]');
        distance.hidden = false;
        const away = fill(text.textAway, { distance: formatMiles(miles) });
        distance.textContent = away.charAt(0).toUpperCase() + away.slice(1);
      });

      const nearest = ranked[0];
      const far = nearest.miles > Number(text.farMiles);
      const message = fill(escapeHtml(far ? text.textFar : text.textResult), {
        origin: `<strong>${escapeHtml(originLabel)}</strong>`,
        studio: `<strong>${escapeHtml(nearest.el.dataset.name)}</strong>`,
        distance: escapeHtml(formatMiles(nearest.miles)),
      });
      // "Upper East Side - NYC" -> "UPPER EAST SIDE" for the button label
      const shortName = nearest.el.dataset.name.replace(/\s*-\s*[^-]*$/, '').toUpperCase();
      const url = far ? text.farUrl : nearest.el.dataset.book;
      const label = far ? text.farLabel : fill(text.textResultBook, { studio: shortName });
      showMessage(
        `${message}
        ${url ? `<span class="london-studios__result-actions"><a href="${escapeHtml(url)}" class="custom-second-link flex">${escapeHtml(label)}</a></span>` : ''}`
      );
    }

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const value = input.value.trim();
      if (!value) {
        showMessage(escapeHtml(text.textEmpty), true);
        input.focus();
        return;
      }
      setLoading(true);
      try {
        const origin = await geocodeZip(value);
        rank(origin, origin.label);
      } catch (err) {
        const messages = { invalid: text.textInvalid, 'not-found': text.textNotFound };
        showMessage(escapeHtml(messages[err.message] || text.textService), true);
      } finally {
        submit.disabled = false;
        if (geoBtn) geoBtn.disabled = false;
      }
    });

    if (geoBtn && 'geolocation' in navigator) {
      geoBtn.hidden = false;
      geoBtn.addEventListener('click', () => {
        setLoading(true);
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            setLoading(false);
            rank({ lat: pos.coords.latitude, lng: pos.coords.longitude }, text.textGeoOrigin);
          },
          () => {
            setLoading(false);
            showMessage(escapeHtml(text.textGeoError), true);
          },
          { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
        );
      });
    }
  }
  // Phone numbers aren't shown on this page, and some studios have no published hours, so
  // drop the empty headings the shared VIEW DETAILS modal (section-locations-block.js)
  // renders on click.
  document.addEventListener('click', (e) => {
    const button = e.target.closest('[data-london-studios] .locations-block__btn');
    if (!button) return;
    const noHours = !button.dataset.openHFirst && !button.dataset.openHSecond;
    document.querySelectorAll('.modal-universal__content .addresses-modal__small-title').forEach((heading) => {
      const title = heading.textContent.trim();
      if (title !== 'CONTACT' && !(noHours && title === button.dataset.labelHours)) return;
      heading.style.display = 'none';
      if (heading.nextElementSibling) heading.nextElementSibling.style.display = 'none';
    });
  });

  function initAll() {
    document.querySelectorAll('[data-london-studios]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('[data-london-studios]');
    if (section) init(section);
  });
})();